import { useMemo, useState } from "react";
import { ChevronUp, ChevronDown, Crosshair, Shield, Zap, Flame, Users as UsersIcon } from "lucide-react";

/**
 * Rich scoreboard surfaced on the Results page when a Frostbite / Bifrost
 * CRCON pull has populated `fixture.crcon_last_pull.rows`.
 *
 * Shows every player on the server with:
 *   handle · team · faction · squad · role · K · D · K/D · streaks · scores · won
 *
 * Sortable by any numeric column (click a header). Teams tinted red/blue so
 * the eye can parse 85 rows at a glance.
 */
const COLS = [
  { key: "handle",         label: "Player",   align: "left",  numeric: false },
  { key: "team",           label: "Team",     align: "left",  numeric: false },
  { key: "squad",          label: "Squad",    align: "left",  numeric: false },
  { key: "role",           label: "Role",     align: "left",  numeric: false },
  { key: "kills",          label: "K",        align: "right", numeric: true, icon: Crosshair },
  { key: "deaths",         label: "D",        align: "right", numeric: true },
  { key: "kd",             label: "K/D",      align: "right", numeric: true },
  { key: "kill_streak",    label: "KS",       align: "right", numeric: true, icon: Flame },
  { key: "combat_score",   label: "Combat",   align: "right", numeric: true },
  { key: "offense_score",  label: "OFF",      align: "right", numeric: true },
  { key: "defense_score",  label: "DEF",      align: "right", numeric: true, icon: Shield },
  { key: "support_score",  label: "SUP",      align: "right", numeric: true },
  { key: "score",          label: "Total",    align: "right", numeric: true, icon: Zap },
];

function teamClass(team) {
  if (!team) return "text-gray-400";
  const t = team.toLowerCase();
  if (t === "axis" || t === "germany") return "text-red-400";
  if (t === "allies" || t === "usa" || t === "us" || t === "uk" || t === "ussr") return "text-blue-400";
  return "text-gray-300";
}

export default function MatchScoreboard({ rows, pulledAt, source }) {
  const [sortKey, setSortKey] = useState("kills");
  const [sortDir, setSortDir] = useState("desc");
  const [teamFilter, setTeamFilter] = useState("all");

  const teams = useMemo(() => {
    const seen = new Set();
    rows.forEach((r) => r.team && seen.add(r.team));
    return Array.from(seen);
  }, [rows]);

  const filtered = useMemo(() => {
    const base = teamFilter === "all" ? rows : rows.filter((r) => r.team === teamFilter);
    const copy = [...base];
    copy.sort((a, b) => {
      const va = a?.[sortKey], vb = b?.[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sortDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
    return copy;
  }, [rows, sortKey, sortDir, teamFilter]);

  const totalKills = rows.reduce((s, r) => s + (r.kills || 0), 0);
  const topKills = Math.max(...rows.map((r) => r.kills || 0), 0);

  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setSortDir("desc");
    }
  };

  if (!rows?.length) {
    return (
      <div className="border border-[#232330] px-4 py-4 text-xs font-mono text-gray-500" data-testid="match-scoreboard-empty">
        No scoreboard data yet — pull a CRCON link to populate.
      </div>
    );
  }

  return (
    <div className="border border-[#232330] bg-[#07070c]" data-testid="match-scoreboard">
      <div className="px-4 py-3 border-b border-[#232330] bg-[#0a0a12] flex items-center gap-3 flex-wrap">
        <UsersIcon className="w-4 h-4 text-cyan-400" />
        <span className="font-heading font-black uppercase tracking-tight">Full scoreboard</span>
        <span className="font-mono text-[10px] text-gray-500">
          · {rows.length} players · {totalKills} total kills
        </span>
        {pulledAt && (
          <span className="font-mono text-[9px] text-gray-600 ml-auto">
            Pulled {new Date(pulledAt).toLocaleString()}
          </span>
        )}
      </div>

      {teams.length > 1 && (
        <div className="px-4 py-2 border-b border-[#232330] flex items-center gap-1 flex-wrap" data-testid="scoreboard-team-filter">
          <button
            type="button"
            onClick={() => setTeamFilter("all")}
            data-testid="scoreboard-filter-all"
            className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
              teamFilter === "all"
                ? "border-cyan-400 text-cyan-400 bg-cyan-400/10"
                : "border-[#232330] text-gray-500 hover:text-gray-300"
            }`}
          >
            All ({rows.length})
          </button>
          {teams.map((t) => {
            const count = rows.filter((r) => r.team === t).length;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTeamFilter(t)}
                data-testid={`scoreboard-filter-${t}`}
                className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
                  teamFilter === t
                    ? `border-current ${teamClass(t)} bg-white/5`
                    : `border-[#232330] text-gray-500 hover:${teamClass(t)}`
                }`}
              >
                {t} ({count})
              </button>
            );
          })}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead className="border-b border-[#232330] bg-[#0a0a12]">
            <tr>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  data-testid={`scoreboard-sort-${c.key}`}
                  className={`px-3 py-2 cursor-pointer select-none hover:text-cyan-400 transition-colors text-[9px] uppercase tracking-widest text-gray-500 ${
                    c.align === "right" ? "text-right" : "text-left"
                  } ${sortKey === c.key ? "text-cyan-400" : ""}`}
                >
                  <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "flex-row-reverse" : ""}`}>
                    {c.icon && <c.icon className="w-3 h-3" />}
                    {c.label}
                    {sortKey === c.key && (
                      sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const kdColor =
                (r.kd || 0) >= 2 ? "text-[#F4D03F]" :
                (r.kd || 0) >= 1.2 ? "text-cyan-400" :
                (r.kd || 0) >= 1   ? "text-gray-300" :
                "text-gray-500";
              const bar = topKills > 0 ? Math.round(((r.kills || 0) / topKills) * 100) : 0;
              return (
                <tr
                  key={`${r.handle}-${i}`}
                  data-testid={`scoreboard-row-${r.handle}`}
                  className={`border-b border-[#232330]/60 ${r.won ? "bg-cyan-400/[0.015]" : ""} hover:bg-[#101015]`}
                >
                  <td className="px-3 py-1.5 text-white font-bold whitespace-nowrap">
                    <span className="relative inline-block">
                      <span className="absolute left-0 top-0 h-full bg-[#FF007F]/10" style={{ width: `${bar}%` }} />
                      <span className="relative">{r.handle}</span>
                    </span>
                  </td>
                  <td className={`px-3 py-1.5 ${teamClass(r.team)}`}>{r.team || "—"}</td>
                  <td className="px-3 py-1.5 text-gray-400 uppercase">{r.squad || "—"}</td>
                  <td className="px-3 py-1.5 text-gray-400">{r.role || "—"}</td>
                  <td className="px-3 py-1.5 text-right text-white">{r.kills ?? 0}</td>
                  <td className="px-3 py-1.5 text-right text-gray-400">{r.deaths ?? 0}</td>
                  <td className={`px-3 py-1.5 text-right font-bold ${kdColor}`}>
                    {typeof r.kd === "number" ? r.kd.toFixed(2) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right text-[#F4D03F]/80">{r.kill_streak ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right text-gray-400">{r.combat_score ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right text-gray-400">{r.offense_score ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right text-gray-400">{r.defense_score ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right text-gray-400">{r.support_score ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right text-cyan-400 font-bold">{r.score ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {source && (
        <div className="px-4 py-2 border-t border-[#232330] text-[9px] font-mono text-gray-600 break-all">
          Source: <a href={source} target="_blank" rel="noreferrer" className="hover:text-cyan-400 underline">{source}</a>
        </div>
      )}
    </div>
  );
}
